import { motion, useReducedMotion } from 'framer-motion'
import type { AvatarState } from '../state/chatStore'
import { useT, type UiStrings } from '../lib/i18n'

/**
 * The figure from the K4ia logo. One file for every size; replacing it with a
 * higher-resolution export changes the avatar everywhere (ADR 0011).
 */
export const AVATAR_SRC = '/brand/k4ia-avatar.png'

export const STATE_LABELS: Record<AvatarState, string> = {
  idle: 'K4ia wartet',
  listening: 'K4ia hört zu',
  thinking: 'K4ia denkt nach',
  speaking: 'K4ia spricht',
  error: 'K4ia hat ein Problem festgestellt',
}

/** Die Zustandsbeschriftung in der Sprache des Besuchers. */
export function stateLabel(state: AvatarState, t: UiStrings): string {
  switch (state) {
    case 'idle':
      return t.stateIdle
    case 'listening':
      return t.stateListening
    case 'thinking':
      return t.stateThinking
    case 'speaking':
      return t.stateSpeaking
    case 'error':
      return t.stateError
  }
}

interface StateMotion {
  /** Seconds per full turn of the ray ring; 0 stands still. */
  spin: number
  /** Opacity of the ray ring — how present K4ia's attention is. */
  rays: number
  /** Scale loop of the ray ring and its duration. */
  pulse: number[]
  pulseDuration: number
  /** Motion of the figure itself. */
  figure: { scale?: number[]; rotate?: number[]; y?: number[] }
  figureDuration: number
}

const STATE_MOTION: Record<AvatarState, StateMotion> = {
  // Calm: the rays drift, the figure breathes.
  idle: {
    spin: 60,
    rays: 0.35,
    pulse: [1, 1.02, 1],
    pulseDuration: 4,
    figure: { scale: [1, 1.015, 1] },
    figureDuration: 4,
  },
  // Attentive: the rays light up and pulse towards the visitor.
  listening: {
    spin: 30,
    rays: 0.9,
    pulse: [0.97, 1.06, 0.97],
    pulseDuration: 0.9,
    figure: { rotate: [0, -4, 0] },
    figureDuration: 1.8,
  },
  // Pondering: the rays turn quickly, the figure tilts its head.
  thinking: {
    spin: 6,
    rays: 0.6,
    pulse: [1, 1, 1],
    pulseDuration: 1,
    figure: { rotate: [-3, 3, -3], y: [0, -2, 0] },
    figureDuration: 2.2,
  },
  // Talking: short pulses in the rhythm of speech.
  speaking: {
    spin: 20,
    rays: 0.9,
    pulse: [0.97, 1.07, 0.97],
    pulseDuration: 0.5,
    figure: { scale: [1, 1.03, 1] },
    figureDuration: 0.5,
  },
  // Problem: everything stops, rays turn red, the figure greys out.
  error: {
    spin: 0,
    rays: 0.75,
    pulse: [1, 1, 1],
    pulseDuration: 1,
    figure: {},
    figureDuration: 1,
  },
}

const RAY_COUNT = 16
const RAY_INNER = 30
const RAY_OUTER = 50

/** Wedge-shaped rays, like the sunburst behind the figure in the logo. */
const RAY_PATHS = Array.from({ length: RAY_COUNT }, (_, i) => {
  const half = Math.PI / (RAY_COUNT * 2)
  const a = (i / RAY_COUNT) * Math.PI * 2
  const pt = (r: number, ang: number) =>
    `${(Math.cos(ang) * r).toFixed(2)} ${(Math.sin(ang) * r).toFixed(2)}`
  return `M${pt(RAY_INNER, a - half * 0.6)} L${pt(RAY_OUTER, a - half)} A${RAY_OUTER} ${RAY_OUTER} 0 0 1 ${pt(RAY_OUTER, a + half)} L${pt(RAY_INNER, a + half * 0.6)} Z`
})

/**
 * K4ia's avatar (ADR 0011): the figure from the logo inside a ring of rays in
 * the j4p cyan. `state` drives distinct motion per
 * idle/listening/thinking/speaking/error. With reduced motion the state stays
 * visible through ray brightness and colour alone, and the caption says it in
 * words — colour is never the only signal (WCAG 1.4.1).
 */
export function Avatar({
  state,
  size = 96,
  /**
   * Lautstärke des Mikrofons, 0…1. Legt im Zustand „hört zu" einen Ring um
   * die Figur, der mit der Stimme mitgeht.
   *
   * Ohne diese Rückmeldung steht man im Showroom vor einem Bildschirm und
   * weiß nicht, ob man gehört wird — man redet lauter, wiederholt sich, gibt
   * auf. Eine Anzeige, die auf die eigene Stimme reagiert, beantwortet das
   * ohne ein Wort.
   */
  micLevel = 0,
  /**
   * The visible state caption. Off where it would only repeat what a heading
   * right next to it already says (the kiosk attract screen) — the accessible
   * name stays on the container either way.
   */
  showLabel = true,
}: {
  state: AvatarState
  size?: number
  showLabel?: boolean
  micLevel?: number
}) {
  const reduceMotion = useReducedMotion()
  const m = STATE_MOTION[state]
  const isError = state === 'error'
  // Nur beim Zuhören: Während K4ia spricht, wäre ein mitlaufender Ring
  // verwirrend — er gehört zur Stimme des Besuchers, nicht zu ihrer.
  const zeigtPegel = state === 'listening'
  // The figure covers the inner part of the rays; the ring shows around it.
  // Small avatars (chat header) get a thinner ring so the figure stays legible.
  const figureInset = Math.round(size * (size < 64 ? 0.09 : 0.14))
  const t = useT()
  const loop = (duration: number) =>
    reduceMotion ? { duration: 0 } : { duration, repeat: Infinity, ease: 'easeInOut' as const }

  return (
    <div className="flex flex-col items-center gap-2" role="img" aria-label={stateLabel(state, t)}>
      <div className="relative" style={{ width: size, height: size }}>
        {zeigtPegel && (
          <motion.div
            aria-hidden
            className="absolute inset-0 rounded-full border-2"
            style={{ borderColor: 'var(--color-accent)' }}
            // Kein repeat: Der Ring folgt der Stimme, er pulsiert nicht von
            // selbst. Sonst sähe Stille aus wie Sprechen.
            animate={{ scale: 1 + micLevel * 0.45, opacity: 0.25 + micLevel * 0.75 }}
            transition={{ duration: 0.12, ease: 'easeOut' }}
          />
        )}
        <motion.div
          className="absolute inset-0"
          animate={{ rotate: reduceMotion || m.spin === 0 ? 0 : 360 }}
          transition={
            reduceMotion || m.spin === 0
              ? { duration: 0 }
              : { duration: m.spin, repeat: Infinity, ease: 'linear' }
          }
        >
          <motion.svg
            viewBox="-50 -50 100 100"
            className="h-full w-full"
            aria-hidden="true"
            animate={{ scale: reduceMotion ? 1 : m.pulse, opacity: m.rays }}
            transition={{
              scale: loop(m.pulseDuration),
              opacity: { duration: 0.4 },
            }}
          >
            {RAY_PATHS.map((d, i) => (
              <path
                key={i}
                d={d}
                fill={isError ? 'var(--color-status-error)' : 'var(--color-accent)'}
              />
            ))}
          </motion.svg>
        </motion.div>
        <motion.img
          src={AVATAR_SRC}
          alt=""
          draggable={false}
          className="absolute select-none rounded-full"
          style={{
            inset: figureInset,
            width: size - figureInset * 2,
            height: size - figureInset * 2,
            boxShadow: '0 2px 12px rgba(43, 43, 43, 0.18)',
            filter: isError ? 'grayscale(0.85)' : 'none',
            transition: 'filter 400ms ease',
          }}
          animate={
            reduceMotion
              ? { scale: 1, rotate: 0, y: 0 }
              : { scale: 1, rotate: 0, y: 0, ...m.figure }
          }
          transition={loop(m.figureDuration)}
        />
      </div>
      {showLabel && (
        <span className="font-mono text-xs text-text-muted">
          {/* Nur der Zustand, nicht der Pegel: „hört dich" sprang mit jeder
              Silbe zwischen zwei Texten hin und her. Dass sie dich hört, zeigt
              der Ring — der darf zittern, Text nicht. */}
          {stateLabel(state, t)}
        </span>
      )}
    </div>
  )
}
